<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         :showCloseButton="!dataIsSending"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">{{ $t('messageSendModalTitle') }}</div>
    </template>
    <Spinner v-show="dataIsSending"/>
    <div v-if="sendWarning" class="p-p-3 p-mb-3" role="alert" style="background: #fff4dc; color: #624400; border-radius: 4px;">
      {{ sendStatusText(sendWarning) }}
    </div>
    <div v-show="!dataIsSending" class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="message_sender_modal"
                          :class="{'p-invalid' : submitted && !selectedSender}"
                          v-model="selectedSender"
                          :options="$store.state.user.emailAccounts"
                          :clearSearchData="!visible"
                          :filterFields="['username']"
                          :filter="true"
                          optionLabel="username"
                          :showClear="false">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span>{{ slotProps.value.username }}</span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div>
                <span>{{ slotProps.option.username }}</span>
              </div>
            </template>
          </CustomDropdown>
          <label for="message_sender_modal">{{ $t('Sender') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !selectedSender">{{ $t('Required field') }}</small>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText :class="{'p-invalid' : submitted && (!emailData.to_email || !receiverEmailIsValid) }" id="message_receiver_modal" v-model="emailData.to_email" @input="validateEmail($event.target.value)" autocomplete="off"/>
          <label for="message_receiver_modal">{{ $t('To') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !emailData.to_email">{{ $t('Required field') }}</small>
        <small class="p-invalid" v-else-if="submitted && emailData.to_email && !receiverEmailIsValid">{{ $t('Value is not a valid email address') }}</small>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText :class="{'p-invalid' : submitted && !emailData.subject}" id="message_subject_modal" v-model.trim="emailData.subject" autocomplete="off"/>
          <label for="message_subject_modal">{{ $t('Subject') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !emailData.subject">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <ModifiedEditor @text-change="textOnChange"
                        id="email-editor"
                        v-model="messageText"
                        :clearMessageText="!visible"
                        editorStyle="height: 260px">
          <template #toolbar>
          </template>
        </ModifiedEditor>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12" v-show="canUseEmailAttachments && emailData.attachments?.length">
        <div class="p-d-flex p-ai-center p-mb-3" v-if="!emailData.fromOrderFilesPage">
          <InputSwitch id="attach-files-switch" v-model="attachFiles"/>
          <label for="attach-files-switch" class="switch-label pointer">{{ $t('attachOriginalMessageFilesLabel') }}</label>
        </div>
        <div v-if="attachFiles">
          <div v-for="attachment of emailData.attachments" :key="attachment.id" class="p-d-flex p-ai-center">
            <div class="p-mr-1">
              <Button :disabled="attachment.is_removed && computedTotalFilesLength >= maxFilesCount" @click="toggleAttachmentStatus(attachment)" :icon="attachment.is_removed ? 'ti-plus' : 'ti-close'" class="p-button-text"></Button>
            </div>
            <div class="p-ml-1" :class="{'removed-attachment' : attachment.is_removed}">
              <!--              <span style="word-break: break-all;">{{ attachment.source_name }}</span>, {{ $t('Size') }}: {{ formatDecimal(+attachment.size / 1000000) }} MB-->
              <span style="word-break: break-all;">{{ attachment.source_name }}</span><span v-if="attachment.size">, {{ $t('Size') }}: {{ formatSize(attachment.size) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-d-flex p-ai-center p-flex-wrap"
           v-if="(emailData.isOffer || emailData.isInvoice) && (showAttachDocumentButton || showSendSmsButton)">
        <div v-if="showAttachDocumentButton" class="p-d-flex p-ai-center p-mr-3 p-mb-3">
          <InputSwitch id="send-document-switch" v-model="attachDocument"/>
          <label for="send-document-switch" class="switch-label pointer">{{ $t('attachPdfLabel') }}</label>
        </div>
        <div v-if="showSendSmsButton" class="p-d-flex p-ai-center p-mb-3">
          <InputSwitch id="send-sms-switch" v-model="sendSms"/>
          <label for="send-sms-switch" class="switch-label pointer">{{ $t('sendSmsLabel') }}</label>
        </div>
      </div>

      <div v-if="billingEntitlementsReady && emailData.isOffer && !canUseBookingFromOffer"
           class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="booking-from-offer-promo">
          <div class="booking-from-offer-promo__title">
            <i class="ti-lock p-text-secondary"/>
            {{ $t('billing.features.booking_from_offer.emailPromoTitle') }}
          </div>

          <div class="p-mt-2">
            <span class="booking-from-offer-promo__plan">
              {{ $t('billing.featureGate.availableFromPlanLabel', {plan: billingRequiredPlanKey('booking_from_offer')}) }}
            </span>

            <span v-if="$store.getters.currentLocationPlanKey"
                  class="p-text-secondary">
                  ({{ $t('billing.featureGate.currentPlanLabel', {plan: $store.getters.currentLocationPlanKey})}})
            </span>
          </div>

          <div class="booking-from-offer-promo__description">
            {{ $t('billing.features.booking_from_offer.tooltip') }}
          </div>

          <!--          <Button v-if="$store.getters.isOwnerOrHigher"-->
          <!--                  style="width: auto"-->
          <!--                  :label="$t('billing.featureGate.changePlanLabel')"-->
          <!--                  icon="ti-arrow-right"-->
          <!--                  iconPos="right"-->
          <!--                  class="p-button-sm p-button-outlined booking-from-offer-promo__button"-->
          <!--                  @click="openBillingFeaturePlans('booking_from_offer')"/>-->
          <!--          <div v-else class="booking-from-offer-promo__owner-hint">-->
          <!--            <i class="pi pi-info-circle"/>-->
          <!--            {{ $t('billing.featureGate.contactOwnerMsg') }}-->
          <!--          </div>-->

          <!--          <div v-if="$store.getters.isOwnerOrHigher" class="p-mt-3">-->
          <!--            <a href="#"-->
          <!--               class="p-mt-3"-->
          <!--               draggable="false"-->
          <!--               @click.prevent="openBillingFeaturePlans('booking_from_offer')">-->
          <!--              <div class="p-d-flex p-ai-center">-->
          <!--                {{ $t('billing.featureGate.changePlanLabel') }}-->
          <!--                <i class="ti-arrow-right p-ml-1"/>-->
          <!--              </div>-->
          <!--            </a>-->
          <!--          </div>-->

          <div v-if="$store.getters.isOwnerOrHigher" class="p-mt-2">
            <a href="#"
               class="booking-from-offer-promo__link"
               draggable="false"
               @click.prevent="openBillingFeaturePlans('booking_from_offer')">
              {{ $t('billing.featureGate.changePlanLabel') }}
              <i class="ti-arrow-right p-ml-1"/>
            </a>
          </div>
          <div v-else class="p-d-flex p-ai-center p-text-secondary p-mt-3">
            <i class="pi pi-info-circle p-mr-1"/>
            {{ $t('billing.featureGate.contactOwnerMsg') }}
          </div>

        </div>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5 p-mt-3" v-if="sendSms">
        <div>
          <LimitedCharsTextarea :submitted="submitted"
                                :rows="5"
                                disabled
                                v-model="smsText"
                                :maxChars="settings.maxSmsMessageLength"
                                :id="'email-sms-message-text'"
                                :label="$t('smsMessageTextLabel')"/>
        </div>
        <small v-if="emailData?.smsUserData?.phoneNumberString" class="p-text-secondary">{{ $t('recipientLabel') }}: {{emailData.smsUserData.phoneNumberString}}<span v-if="emailData.smsUserData.fullName">, {{ emailData.smsUserData.fullName }}</span></small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12" v-if="computedAttachedFilesTotalSize">
        <span :class="computedAttachedFilesTotalSize > maxMessageSize ? 'warning-color' : 'green-color'">
          {{ $t('Size') }}: {{ formatSize(computedAttachedFilesTotalSize) }} / {{ formatSize(maxMessageSize) }}
        </span>
        <span v-show="computedTotalFilesLength" class="p-ml-2">{{ $t('Files') }}: {{ computedTotalFilesLength }}/{{ maxFilesCount }}</span>
      </div>

      <div v-if="canUseEmailAttachments" class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <FileUploader :item="{ id: null }"
                      @select="onFileSelect"
                      @clear="onFilesClear"
                      @remove="onFileRemove"
                      ref="fileUploader"
                      field="email_message_files"
                      :multiple="true"
                      :allowNonImages="true"
                      :imagesOnly="false"
                      :itemWidth="130"
                      :itemHeight="130"
                      :maxFiles="maxFilesCount"
                      :maxFilesPerUpload="maxFilesCount"
                      :existingFilesCount="computedActiveAttachmentsLength"
                      :maxSizeMb="10"
                      :maxWidth="1600"
                      :maxHeight="1600"
                      :jpegQuality="0.82"
                      resizeMode="contain"/>
      </div>
      <div v-else class="p-field p-col-12">
        <BillingFeatureHint feature="email_attachments"
                            message-key="billing.features.email_attachments.availableHint"
                            class="p-d-block"/>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')"
              :disabled="dataIsSending"
              icon="pi pi-times"
              class="p-button-text"
              @click="close"/>
      <Button :label="$t('Send')"
              :loading="dataIsSending"
              :disabled="disableSaveButton"
              icon="pi pi-send"
              class="p-button-text"
              @click="sendMessage"/>
    </template>
  </Modal>
</template>
<script>
import httpClient from '@/services/http.services'
import mailSendStatus from '@/translations/mailSendStatus'
import ModifiedEditor from "@/components/ModifiedEditor";
import formatMixins from "@/mixins/formatMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import FileUploader from "@/components/FileUploader";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import BillingFeatureHint from "@/components/billing/BillingFeatureHint.vue";

export default {
  mixins: [ formatMixins, showErrorsMixins, billingFeaturesMixins ],
  components: { ModifiedEditor, FileUploader, BillingFeatureHint },
  // emits: ['close', 'update-items', 'update-item', 'update-message'],
  emits: ['close', 'update-message', 'email-sent'],
  name: 'EmailModal',
  props: {
    modalLayer: {
      type: Number,
      default: 1
    },
    item: {
      type: Object,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    offerData: {
      type: Object,
      default: null,
    },
    selectedEmailId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      sendWarning: null,
      sendGeneration: 0,
      maxFilesCount: 10,
      maxMessageSize: 15000000, // bytes
      dataIsSending: false,
      disableSaveButton: false,
      smsText: null,
      attachDocument: true,
      showAttachDocumentButton: true,
      sendSms: false,
      showSendSmsButton: true,
      attachFiles: true,
      selectedSender: null,
      emailData: {},
      signature: '',
      submitted: false,
      attachments: [],
      receiverEmailIsValid: false,
      messageText: null,
      file: null,
      settings
    }
  },
  watch: {
    dataIsSending(value) {
      if (value) window.addEventListener('beforeunload', this.warnBeforeUnload)
      else window.removeEventListener('beforeunload', this.warnBeforeUnload)
    },
    item(value) {
      if (!value) {
        this.emailData = {}
        this.messageText = null
        this.selectedSender = null
        return
      }

      let signature = ''

      const userEmailAccounts = this.$store.state.user?.emailAccounts

      if (userEmailAccounts?.length) {
        const firstAccount = userEmailAccounts[0] ?? null
        const findById = (id) => userEmailAccounts.find(a => +a.id === +id) ?? null

        let chosen = firstAccount

        if (this.selectedEmailId) {
          chosen = findById(this.selectedEmailId) || firstAccount
        } else if (value.isOffer) {
          chosen = findById(this.$store.getters.locationData?.estimates_email_account_id) || firstAccount
        } else if (value.isInvoice) {
          chosen = findById(this.$store.getters.locationData?.invoices_email_account_id) || firstAccount
        }

        this.selectedSender = chosen
        signature = chosen?.signature ?? ''
      }

      this.emailData = { ...value }

      if (value.forward) {
        this.emailData.to_email = null
      }

      if (settings.isDevMode) {
        // this.emailData.to_email = settings.isDemoMode ? '' : 'info@topsoft.ee'
        // this.emailData.to_email = settings.isDemoMode ? 'demo@virtualgarage.app' : 'info@topsoft.ee'
        this.emailData.to_email = 'info@topsoft.ee'
        // this.emailData.to_email = ''
      }

      if (this.emailData.to_email) {
        this.validateEmail(this.emailData.to_email)
      }

      if (value.subject) {
        if (value.reply) {
          this.emailData.subject = 'Re: ' + value.subject
        } else if (value.forward) {
          this.emailData.subject = 'Fwd: ' + value.subject
        }
      }

      // if ((value.forward || value.fromOrderFilesPage) && value.attachments?.length) {
      if (this.canUseEmailAttachments && (value.forward || value.fromOrderFilesPage) && value.attachments?.length) {
        this.attachFiles = true
        this.emailData.attachments = value.attachments.map((a) => {
          return {
            ...a,
            is_removed: false
          }
        })
      } else {
        this.attachFiles = false
        this.emailData.attachments = []
      }

      let message = '<br>'

      // if (value.isOffer || value.isInvoice || value.forward) {
      if (value.isOffer || value.isInvoice || value.fromOrderFilesPage) {
        message = ''
      } else if (value.forward) {
        message += '<br>'
      }

      if (value.invoice_text) {
        message += `<div style="white-space: pre-wrap">${value.invoice_text}</div>`
      }

      if (value.offer_id) {
        if (value.greeting) {
          message += `<div style="white-space: pre-wrap">${value.greeting}</div>`
        }
        if (value.offer_text) {
          message += `<div style="white-space: pre-wrap">${value.offer_text}</div>`
        }

        message += '<br>'

        if (this.canUseBookingFromOffer && value.offer_link_text && value.offer_link && value.offer_pin_text && value.offer_pin) {
          message += `<div style="white-space: pre-wrap">${value.offer_link_text}:</div>
                      <div style="white-space: pre-wrap">${ value.offer_link }</div>
                      <div style="white-space: pre-wrap"><strong>${value.offer_pin_text}: ${value.offer_pin}</strong></div>`

        }

        // const containsActivePositionBoolean = !!value.containsActivePositions && value.offer_file_path && (value.offer_state === 3 || value.offer_state === 5 || value.offer_state === 6)

        const containsActivePositionBoolean =
            !!value.containsActivePositions &&
            !!value.offer_file_path &&
            ![1, 7].includes(+value.offer_state)

        this.attachDocument = containsActivePositionBoolean
        this.showAttachDocumentButton = containsActivePositionBoolean

        if (this.canUseBookingFromOffer && value.sms_text) {
          this.smsText = value.sms_text
          this.sendSms = containsActivePositionBoolean
          this.showSendSmsButton = containsActivePositionBoolean
        } else {
          this.sendSms = false
          this.showSendSmsButton = false
          this.smsText = null
        }
      } else {
        this.sendSms = false
        this.showSendSmsButton = false
        this.smsText = null
      }

      message += '<br>'  //don't delete!!
      // message += '<br>'  //don't delete!!

      // if (!value.forward && signature) {
      if (signature) {
        message += `<div style="white-space: pre-wrap">${signature}</div><br>`
      }

      if (value.reply) {
        // message += `<i>On ${value.created_at ? this.formatDate(value.created_at) : ''}, ${value.from_name} &lt;${value.from_email}&gt; wrote:</i><br><br>`
        message += `<i>On ${value.created_at ? this.formatDate(value.created_at) : ''}, ${this.escapeHtml(value.from_name ?? '')} &lt;${this.escapeHtml(value.from_email ?? '')}&gt; wrote:</i><br><br>`
      } else if (value.forward) {
        // message += '<div>---------- Forwarded Message ----------</div>' +
        //            `<div>From: ${value.from_name ?? ''} &lt;${value.from_email ?? ''}&gt; ` +
        //            `<div>Date: ${this.formatDate(value.created_at)}` +
        //            `<div>Subject: ${value.subject || ''}` +
        //            `<div>To: ${value.to_name ?? ''} &lt;${value.to_email ?? ''}&gt; <br><br>`

        message += `<div>---------- Forwarded Message ----------</div>
                    <div>From: ${this.escapeHtml(value.from_name ?? '')} &lt;${this.escapeHtml(value.from_email ?? '')}&gt;</div>
                    <div>Date: ${value.created_at ? this.formatDate(value.created_at) : ''}</div>
                    <div>Subject: ${this.escapeHtml(value.subject || '')}</div>
                    <div>To: ${this.escapeHtml(value.to_name ?? '')} &lt;${this.escapeHtml(value.to_email ?? '')}&gt;</div>
                    <br>`
      }

      if (value.html_text) {
        // message += value.html_text
        message += this.sanitizeEmailHtml(value.html_text)
      } else if (value.plain_text) {
        message += `<br><div style="white-space: pre-wrap">${this.escapeHtml(value.plain_text)}</div><br><br>`
      } else {
        message += '<div></div>'
      }


      this.messageText = message
    },
    visible() {
      if (!this.visible) {
        this.sendGeneration++
        this.sendWarning = null
        this.disableSaveButton = false
        this.dataIsSending = false
        this.submitted = false
        this.selectedSender = null

        this.attachFiles = true

        this.attachDocument = true
        this.sendSms = false

        this.emailData.attachments = []

        this.clearAttachments()
      }
    },
  },
  beforeUnmount() {
    this.sendGeneration++
    window.removeEventListener('beforeunload', this.warnBeforeUnload)
  },
  methods: {
    sendStatusText(key) { return mailSendStatus(this.$i18n.locale, key) },
    warnBeforeUnload(event) {
      if (!this.dataIsSending) return
      event.preventDefault()
      event.returnValue = ''
    },
    showSendError(err) {
      const code = err?.response?.data?.error?.code
      if (code === 'email_sent_but_not_saved') {
        this.sendWarning = 'savedError'
      } else if (code === 'email_delivery_unknown' || !err?.response ||
          err.response.status >= 500 || [408, 499].includes(err.response.status)) {
        // Losing the HTTP response cannot prove that SMTP failed.
        this.sendWarning = 'unknown'
      } else {
        this.showError(err)
      }
    },
    clearAttachments() {
      this.attachments = []
      this.$refs.fileUploader?.clearAttachments()
    },
    toggleAttachmentStatus(attachment) {
      attachment.is_removed = !attachment.is_removed
    },
    textOnChange(data) {
      this.emailData.plainText = data.textValue ?? null
    },
    onFileSelect(data) {
      this.attachments = data?.files || []
    },
    onFilesClear() {
      this.attachments = []
    },
    onFileRemove(data) {
      this.attachments = data?.files || []
    },
    async sendMessage() {
      if (!this.emailData || this.dataIsSending) return
      if (this.sendWarning && !window.confirm(this.sendStatusText('retry'))) return
      this.submitted = true

      if (!this.selectedSender ||
          !this.emailData.to_email ||
          !this.emailData.subject ||
          !this.receiverEmailIsValid) {
        return false
      }

      if (this.computedAttachedFilesTotalSize > this.maxMessageSize) {
        this.$toast.add({severity:'error', summary: this.$t('Error'), detail: this.$t('messageSizeTooBigMsg'), life: this.settings.toastLife})
        return
      }

      this.sendWarning = null
      this.disableSaveButton = true
      this.dataIsSending = true
      const generation = this.sendGeneration

      const plainText = this.emailData.plainText ?? ''

      let formData = new FormData();
      formData.append('from_email', this.selectedSender?.username ?? '')
      formData.append('from_name', this.selectedSender?.sender_name ?? '')
      formData.append('email_account_id', (this.selectedSender?.id ? +this.selectedSender?.id : null) ?? '')
      formData.append('subject', this.emailData.subject ?? '')
      formData.append('html_text', this.messageText ?? '')
      // formData.append('plain_text', this.emailData.htmlText.replace(/<[^>]*(>|$)|&nbsp;|&zwnj;|&raquo;|&laquo;|&gt;/g, ' '))
      formData.append('plain_text', plainText)
      formData.append('to_email', this.emailData.to_email ?? '')
      formData.append('offer_id', this.emailData.offer_id ?? '')
      formData.append('sales_invoice_id', this.emailData.sales_invoice_id ?? '')
      formData.append('prepayment_invoice_id', this.emailData.prepayment_invoice_id ?? '')
      formData.append('attachDocument', this.attachDocument ? 1 : 0)
      formData.append('sendSms', this.sendSms ? 1 : 0)
      formData.append('smsText', this.smsText?.length ? this.smsText : 0)
      formData.append('userId', this.emailData.user_id ?? null)
      formData.append('sms_user_id', this.emailData.sms_user_id ?? null)
      formData.append('locationId', this.$store.getters.locationId ?? null)
      formData.append('fromOrderFilesPage', this.emailData.fromOrderFilesPage ? 1 : 0)

      // let attachedFiles = null
      // if (this.attachFiles && this.emailData?.attachments?.length) {
      //   attachedFiles = this.emailData.attachments.filter(a => !a.is_removed)
      //   formData.append('attachedFiles', JSON.stringify(attachedFiles));
      // }

      if (this.canUseEmailAttachments && this.attachFiles && this.emailData?.attachments?.length) {
        const attachedFiles = this.emailData.attachments.filter(a => !a.is_removed)
        formData.append('attachedFiles', JSON.stringify(attachedFiles))
      }

      if (this.emailData?.reply && this.emailData.id) {
        formData.append('parent_id', this.emailData.id);
      }

      // const attachmentsLength = this.attachments?.length
      // if (attachmentsLength) {
      //   for (let i = 0; i < attachmentsLength; i++)
      //     formData.append(i, this.attachments[i])
      // }

      const attachmentsLength = this.canUseEmailAttachments
          ? this.attachments?.length
          : 0

      if (attachmentsLength) {
        for (let i = 0; i < attachmentsLength; i++) {
          formData.append(i, this.attachments[i])
        }
      }

      const message = {
        email_account_id: this.selectedSender?.id ? +this.selectedSender.id : null,
        is_reply: this.emailData?.reply,
        is_forward: this.emailData?.forward,
        html_text: this.messageText,
        // plain_text: plainText,
      }


      try {
        const { status, data } = await httpClient.post(`mail/send`, formData, {
          timeout: 180000,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (generation !== this.sendGeneration || !this.visible) return
        if (status === 201 && data?.success) {
          let itemId = this.emailData.offer_id ?? this.emailData.sales_invoice_id ?? this.emailData.prepayment_invoice_id


          this.$emit('email-sent', {
            itemId: itemId,

            estimate_pdf_sent: !!data.estimate_pdf_sent,
            sent_at: data.sent_at,
            sent_by: data.sent_by,
            sent_method: data.sent_method,
            state: data.state,

            sales_invoice_id: this.emailData.sales_invoice_id,
            prepayment_invoice_id: this.emailData.prepayment_invoice_id,
          })

          this.$emit('update-message', message)
          this.$toast.add({severity: 'success', detail: this.$t('messageSentMsg'), life: settings.toastLife});
          this.clearAttachments()
          this.close(true)

          if (data.sms_not_sent) {
            this.$toast.add({severity:'error', detail: this.$t('smsSendingErrorMsg'), life: settings.toastLife});
          }
        } else if (data?.error) {
          // const detail = data.error.detail || data.error.summary || data.error
          // this.showError(detail)
          this.showSendError({
            response: {
              status,
              data,
            },
          })
        } else {
          this.sendWarning = 'unknown'
        }
      } catch(err) {
        if (generation === this.sendGeneration && this.visible) this.showSendError(err)
      } finally {
        if (generation === this.sendGeneration) {
          this.dataIsSending = false
          this.disableSaveButton = false
        }
      }
    },
    validateEmail(email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      this.receiverEmailIsValid = re.test(email)
    },
    sanitizeEmailHtml(html) {
      return String(html || '')
          .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
          .replace(/\son\w+="[^"]*"/gi, '')
          .replace(/\son\w+='[^']*'/gi, '')
          .replace(/<img[^>]+src=["']https?:\/\/[^"']+["'][^>]*>/gi, '')
    },
    escapeHtml(value) {
      return String(value || '')
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#039;')
    },
    close(force = false) {
      if (this.dataIsSending && force !== true) return
      this.$emit('close')
      this.submitted = false
    },
  },
  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, width: '95%', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, width: '630px', marginTop: '15px'}
      } else {
        return {position: 'absolute', top: this.modalLayer === 2 ? '15px' : 0, width: '980px'}
      }
    },
    computedTotalFilesLength() {
      if (!this.canUseEmailAttachments) return 0

      return this.computedActiveAttachmentsLength +
          (this.attachments?.length || 0)
    },

    computedActiveAttachmentsLength() {
      if (!this.canUseEmailAttachments) return 0

      const activeAttachments =
          this.emailData.attachments?.filter(a => !a.is_removed)

      return activeAttachments?.length || 0
    },

    computedAttachedFilesTotalSize() {
      if (!this.canUseEmailAttachments) return 0

      let value = 0

      if (this.attachFiles && this.emailData?.attachments?.length) {
        value += this.emailData.attachments
            .filter(a => !a.is_removed)
            .reduce((sum, attachment) => sum + attachment.size, 0)
      }

      if (this.attachments?.length) {
        value += this.attachments
            .reduce((sum, attachment) => sum + attachment.size, 0)
      }

      return value
    },
  },

}
</script>

<style scoped lang="scss">
.removed-attachment {
  text-decoration: line-through;
}

.booking-from-offer-promo {
  padding: 1rem 1.25rem;
  border: 1px solid var(--border-color);
  border-radius: 3px;
  font-size: 0.9rem;

  &__title {
    font-weight: 600;
    color: var(--text-color);
    font-size: 0.94rem;
  }

  &__plan {
    //margin-top: 0.3rem;
    font-weight: 600;
    color: var(--primary-color);
  }

  &__description {
    margin-top: 10px;
    line-height: 1.5;
    color: var(--text-color-secondary);
  }

  //&__button {
  //  margin-top: 10px;
  //}

  &__link {
    display: inline-flex;
    align-items: center;
    width: auto;
    padding: 4px 4px 4px 0;
  }

  //&__owner-hint {
  //margin-top: 1rem;
  //color: var(--text-color-secondary);
  //display: flex;
  //align-items: center;

  //i {
  //  margin-right: 0.35rem;
  //}
  //}
}
</style>